/* eslint-disable complexity */
import { Button, buttonVariants } from '@e2n/ui';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { Abschnitt as AbschnittType } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt';
import {
  faCalendarRange,
  faChevronDown,
  faChevronUp,
  faPenToSquare,
  faPlusLarge,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Link, useParams } from 'react-router-dom';
import { StationContainer } from '@/domain/mitarbeiter/features/rotationsplan/features/Details/StationContainer';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { DeleteAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/features/DeleteAbschnitt/DeleteAbschnitt.tsx';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan.ts';

/* eslint-disable max-lines-per-function */
export function Abschnitt({
  abschnitt,
  rotationsplan,
}: {
  abschnitt: AbschnittType;
  rotationsplan: Rotationsplan;
}) {
  const { id } = useParams();
  const [isOpen, setIsOpen] = useState(true);
  const { t } = useTranslation();

  return (
    <div key={abschnitt.id} className="flex flex-col gap-5">
      <div className="mt-8 flex items-center gap-2 md:hover:bg-neutrals-300 hover:rounded-md px-1 py-1 group">
        <Button
          variant="ghost"
          className="md:hover:bg-neutrals-400"
          size="icon"
          onClick={() => setIsOpen(!isOpen)}>
          <FontAwesomeIcon
            className="text-neutrals-700"
            icon={isOpen ? faChevronUp : faChevronDown}
          />
        </Button>
        <div className="font-bold">
          {abschnitt.position + '. ' + abschnitt.name}
        </div>
        <div className="block md:hidden md:group-hover:block ml-auto md:ml-0">
          <DeleteAbschnitt
            abschnitt={abschnitt}
            rotationsplan={rotationsplan}
          />
        </div>
      </div>
      <div className="flex flex-col">
        {abschnitt.stationen
          .sort((stationA, stationB) => {
            if (stationA.von < stationB.von) return -1;
            if (stationA.von > stationB.von) return 1;
            return 0;
          })
          .map((station) => {
            return station instanceof ArbeitsbereichStation ? (
              isOpen && (
                <StationContainer station={station}>
                  <div
                    key={station.id}
                    className={`mb-4 w-full bg-white rounded-md flex justify-between items-start p-4 ${station.istAktiv ? 'border-primary-blue-600 border-solid border-2 shadow-md' : ''}`}>
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-2 items-center">
                          <FontAwesomeIcon
                            icon={faCircle}
                            color={station.arbeitsbereich.getBorderColor()}
                          />
                          <div className="font-bold">
                            {station.arbeitsbereich.name}
                          </div>
                        </div>
                        {station.lernziele && station.lernziele.length > 0 && (
                          <div className="text-sm text-neutrals-700 line-clamp-3">
                            {station.lernziele}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-start md:flex-row gap-2 md:gap-4 md:items-center text-neutrals-700 text-sm">
                        <div className="flex gap-1 items-center">
                          <FontAwesomeIcon icon={faCalendarRange} />
                          {dayjs(station.von).format('DD.MM.YYYY') +
                            ' – ' +
                            dayjs(station.bis).format('DD.MM.YYYY')}
                        </div>
                        {station.bereichsleiter && (
                          <div className="flex gap-1 items-center">
                            <FontAwesomeIcon icon={faUser} />
                            {station.bereichsleiter.getName()}
                          </div>
                        )}
                        <Location
                          className="text-sm"
                          standort={station.arbeitsbereich.standort}
                        />
                      </div>
                    </div>
                    <Link
                      className={cn(
                        buttonVariants({
                          variant: 'ghost-primary',
                          size: 'icon',
                        }),
                        '-mt-2',
                      )}
                      to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/${station.id}/update`}>
                      <FontAwesomeIcon icon={faPenToSquare} size="lg" />
                    </Link>
                  </div>
                </StationContainer>
              )
            ) : (
              <div key={station.id}>Wird noch nicht angezeigt</div>
            );
          })}
        {isOpen && (
          <StationContainer showGradient>
            <div className="w-full bg-white rounded-md flex justify-start p-4">
              <Link
                to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/create`}
                className={cn(
                  buttonVariants({ variant: 'ghost-primary' }),
                  'flex items-center gap-2',
                )}>
                <FontAwesomeIcon icon={faPlusLarge} />
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.label',
                )}
              </Link>
            </div>
          </StationContainer>
        )}
      </div>
    </div>
  );
}
